/*
 * Admin API client.
 *  DEV  (localhost): same-origin "/api/portal" — the webpack dev server proxies it to func.
 *  PROD (Static Web App): the SWA does NOT proxy /api to the standalone Function App, so we
 *       call the Function App's absolute URL. CORS on the Function App must allow the SWA origin.
 * Every request carries the Entra bearer token (when auth is enabled) for the admin API to validate.
 */

/* global location, fetch */
import { getToken, getSignedInEmail } from "./auth";

const PROD_API_ORIGIN = "https://func-daveysignatures-d8h8dncte7cbdahm.eastus2-01.azurewebsites.net";
const isLocalhost =
  typeof location !== "undefined" && /^(localhost|127\.0\.0\.1)$/i.test(location.hostname);
const BASE = isLocalhost ? "/api/portal" : `${PROD_API_ORIGIN}/api/portal`;

async function authHeaders(extra = {}) {
  const headers = { ...extra };
  const token = await getToken();
  if (token) headers.Authorization = `Bearer ${token}`;
  return headers;
}

async function handle(res) {
  if (!res.ok) {
    let detail = "";
    try {
      detail = (await res.json()).error || "";
    } catch {
      /* ignore */
    }
    throw new Error(`${res.status} ${res.statusText}${detail ? ` — ${detail}` : ""}`);
  }
  return res.json();
}

export async function getConfig() {
  return handle(await fetch(`${BASE}/config`, { headers: await authHeaders({ Accept: "application/json" }) }));
}

export async function saveConfig(config) {
  return handle(
    await fetch(`${BASE}/config`, {
      method: "PUT",
      headers: await authHeaders({ "Content-Type": "application/json" }),
      body: JSON.stringify(config),
    })
  );
}

export async function previewTemplate(html, sampleUser) {
  // Render the preview with the signed-in admin's real Graph data when available,
  // so variables ({{displayName}}, {{jobTitle}}, …) reflect a real mailbox. Falls
  // back to the backend's sample user when not signed in (local dev).
  const email = getSignedInEmail();
  return handle(
    await fetch(`${BASE}/preview`, {
      method: "POST",
      headers: await authHeaders({ "Content-Type": "application/json" }),
      body: JSON.stringify({ html, sampleUser, email: email || undefined }),
    })
  );
}

export async function searchDirectory(type, q) {
  const params = new URLSearchParams({ type, q });
  return handle(await fetch(`${BASE}/directory?${params.toString()}`, { headers: await authHeaders({ Accept: "application/json" }) }));
}

// Distinct officeLocation values from the directory, for the Company tab.
// Cached server-side; pass force=true (Refresh) to re-scan the directory.
export async function getOfficeLocations(force = false) {
  const qs = force ? "?refresh=1" : "";
  return handle(await fetch(`${BASE}/office-locations${qs}`, { headers: await authHeaders({ Accept: "application/json" }) }));
}
